// Small shared presentational primitives for the admin tables. Kept local to
// the admin pages — not part of the public component library.
import type { ComponentChildren, JSX } from 'preact';
import { useEffect } from 'preact/hooks';
import { Button } from '../../components/Button';

export const PAGE_SIZE = 50;

export type Page = { total: number; limit: number; offset: number };

// Shared input/select chrome used across the admin forms (toolbar, gift form,
// modal). One source of truth so every field looks the same.
export const fieldStyle: JSX.CSSProperties = {
  background: 'var(--card)', border: 'none', boxShadow: '0 0 0 1px var(--line)',
  borderRadius: 12, padding: '10px 14px', fontFamily: 'var(--font-ui)',
  fontSize: 14, color: 'var(--ink)', outline: 'none',
};

// A centered overlay dialog. Closes on backdrop click or Escape. No portal —
// a fixed-position overlay renders above everything via z-index, which is all
// the admin pages need.
export function Modal({
  title, onClose, children,
}: { title: string; onClose: () => void; children: ComponentChildren }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--card)', borderRadius: 18, boxShadow: '0 12px 48px rgba(0,0,0,0.28)',
          padding: 22, width: '100%', maxWidth: 440,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{title}</div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 18,
              lineHeight: 1, color: 'var(--ink-3)', padding: 4,
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Table({ children }: { children: ComponentChildren }) {
  return (
    <div style={{ overflowX: 'auto', borderRadius: 16, boxShadow: '0 0 0 1px var(--line)', background: 'var(--card)' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        {children}
      </table>
    </div>
  );
}

export function Th({ children }: { children: ComponentChildren }) {
  return (
    <th
      style={{
        textAlign: 'left', padding: '12px 16px', fontSize: 12, fontWeight: 700,
        color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.04em',
        borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  );
}

export function Td({
  children, muted, colSpan,
}: { children: ComponentChildren; muted?: boolean; colSpan?: number }) {
  return (
    <td
      colSpan={colSpan}
      style={{
        padding: '12px 16px', borderBottom: '1px solid var(--line)',
        color: muted ? 'var(--ink-3)' : 'var(--ink)', verticalAlign: 'middle',
      }}
    >
      {children}
    </td>
  );
}

type Tone = 'green' | 'ember' | 'wine' | 'neutral';
const TONE: Record<Tone, JSX.CSSProperties> = {
  green: { background: 'rgba(34,160,90,0.14)', color: '#1c7a45' },
  ember: { background: 'var(--ember-tint)', color: 'var(--ember)' },
  wine: { background: 'rgba(150,30,60,0.12)', color: 'var(--wine)' },
  neutral: { background: 'var(--surface-2)', color: 'var(--ink-2)' },
};

export function Badge({ children, tone = 'neutral' }: { children: ComponentChildren; tone?: Tone }) {
  return (
    <span
      style={{
        display: 'inline-block', padding: '3px 9px', borderRadius: 999,
        fontSize: 12, fontWeight: 600, ...TONE[tone],
      }}
    >
      {children}
    </span>
  );
}

type FilterDef = {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
};

export function Toolbar({
  search, onSearch, placeholder, filters = [],
}: {
  search: string;
  onSearch: (v: string) => void;
  placeholder: string;
  filters?: FilterDef[];
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
      <input
        type="search"
        value={search}
        onInput={(e) => onSearch((e.target as HTMLInputElement).value)}
        placeholder={placeholder}
        style={{
          flex: '1 1 240px', background: 'var(--card)', border: 'none',
          boxShadow: '0 0 0 1px var(--line)', borderRadius: 12, padding: '10px 14px',
          fontFamily: 'var(--font-ui)', fontSize: 14, color: 'var(--ink)', outline: 'none',
        }}
      />
      {filters.map((f, i) => (
        <select
          key={i}
          value={f.value}
          onChange={(e) => f.onChange((e.target as HTMLSelectElement).value)}
          style={{
            background: 'var(--card)', border: 'none', boxShadow: '0 0 0 1px var(--line)',
            borderRadius: 12, padding: '10px 14px', fontFamily: 'var(--font-ui)',
            fontSize: 14, color: 'var(--ink)', outline: 'none', cursor: 'pointer',
          }}
        >
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ))}
    </div>
  );
}

export function Pager({
  total, offset, count, loading, onOffset,
}: {
  total: number;
  offset: number;
  count: number;
  loading: boolean;
  onOffset: (n: number) => void;
}) {
  const from = total === 0 ? 0 : offset + 1;
  const to = offset + count;
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
      <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>
        {loading ? 'Loading…' : `${from}–${to} of ${total}`}
      </span>
      <div style={{ display: 'flex', gap: 8 }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={offset === 0 || loading}
          onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          ← Prev
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={to >= total || loading}
          onClick={() => onOffset(offset + PAGE_SIZE)}
        >
          Next →
        </Button>
      </div>
    </div>
  );
}

export function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
